import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Target, HeartHandshake, Users, ShieldCheck } from "lucide-react";
import { api, API, BACKEND_URL, formatErr } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import Banner from "@/components/Banner";
import Testimonials from "@/components/Testimonials";
import TrialRequestForm from "@/components/TrialRequestForm";
import PersonalCoachingRequestForm from "@/components/PersonalCoachingRequestForm";
import Navigation from "@/components/Navigation";

const PLAN_META = {
  coaching_1sem_monthly: { tag: "Coaching · abonnement", tagline: "Une séance encadrée par semaine.", highlights: ["Programme personnalisé", "Suivi hebdomadaire", "Progression mesurée"] },
  coaching_2sem_monthly: { tag: "Coaching · abonnement", tagline: "Deux séances encadrées par semaine.", highlights: ["Programme personnalisé", "Progression accélérée", "Suivi rapproché"], featured: true },
  coaching_3sem_monthly: { tag: "Coaching · abonnement", tagline: "Trois séances encadrées par semaine.", highlights: ["Programme personnalisé", "Progression intensive", "Résultats rapides"] },
  coaching_individuel_session: { tag: "Coaching · à l'unité", tagline: "Une séance de coaching d'1h sur mesure.", highlights: ["Créneau exclusif · 1h", "Aucun engagement", "Idéal pour tester ou compléter"], oneTime: true },
  libre_1sem_monthly: { tag: "Accès libre", tagline: "Une séance en autonomie par semaine.", highlights: ["Accès à la salle", "Créneau réservé", "Zéro engagement"] },
  libre_2sem_monthly: { tag: "Accès libre", tagline: "Deux séances en autonomie par semaine.", highlights: ["Accès à la salle", "Deux créneaux/sem", "Zéro engagement"] },
  libre_3sem_monthly: { tag: "Accès libre", tagline: "Trois séances en autonomie par semaine.", highlights: ["Accès à la salle", "Trois créneaux/sem", "Zéro engagement"] },
  remote_decouverte: { tag: "Distanciel", tagline: "Programme personnalisé en ligne · 1x/semaine", highlights: ["Suivi personnalisé", "Programme adapté", "Échanges réguliers"] },
  remote_evolution: { tag: "Distanciel · Populaire", tagline: "Programme évolutif sur mesure · 2x/semaine", highlights: ["Suivi rapproché", "Progression mesurée", "Coaching à distance"], featured: false },
  remote_transformation: { tag: "Distanciel", tagline: "Accompagnement complet · 3 à 5x/semaine", highlights: ["Suivi intensif", "Résultats optimaux", "Transformation complète"] },
};

export default function Home() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [plans, setPlans] = useState([]);
  const [planTab, setPlanTab] = useState("coaching");
  const [gallery, setGallery] = useState([]);
  const [coachStats, setCoachStats] = useState({ since_year: 2021, members_count: 120, sessions_per_year: 1800 });
  const [trialFormOpen, setTrialFormOpen] = useState(false);
  const [personalCoachingFormOpen, setPersonalCoachingFormOpen] = useState(false);
  const [siteConfig, setSiteConfig] = useState(null);

  const handleLogout = async () => {
    try {
      await logout();
      toast.success("Déconnexion réussie");
      nav("/");
    } catch (err) {
      toast.error("Erreur lors de la déconnexion");
    }
  };

  useEffect(() => {
    // Load site config (plans + home content)
    api.get("/config/public").then((r) => {
      setPlans(r.data.plans || []);
      setSiteConfig(r.data);
    }).catch(() => {});
    api.get("/gallery").then((r) => setGallery(r.data)).catch(() => {});
    api.get("/coach-stats").then((r) => setCoachStats(r.data)).catch(() => {});
  }, []);

  const homeContent = siteConfig?.home_content || {};
  const heroTitle = homeContent.hero_title || "Un suivi adaptatif et une communauté soudée.";
  const heroSubtitle = homeContent.hero_subtitle || "Coaching de groupe ou coaching individuel dans mon studio, du lundi au vendredi de 9h à 20h30. Choisissez votre formule, réservez vos créneaux, et progressez à votre rythme.";
  const heroCTAPrimary = homeContent.hero_cta_primary || "Voir les forfaits";
  const heroCTASecondary = homeContent.hero_cta_secondary || "Séance d'essai offerte →";

  return (
    <div className="min-h-screen bg-[#F9F9F6] text-stone-900">
      <Navigation user={user} handleLogout={handleLogout} />
      <Banner />

      {/* HERO */}
      <section id="top" className="grain-bg relative overflow-hidden pt-4">
        <div className="mx-auto grid max-w-7xl items-center gap-12 sm:gap-16 px-4 sm:px-6 py-20 sm:py-24 lg:grid-cols-12 lg:gap-8 lg:px-12 lg:py-32">
          <div className="lg:col-span-7">
            <p data-testid="hero-overline" className="mb-6 sm:mb-8 text-xs font-medium uppercase tracking-[0.32em] text-stone-500">Coaching sportif · Studio privé</p>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-light leading-[0.95] tracking-tight text-stone-900 whitespace-pre-line">
              {heroTitle}
            </h1>
            <p className="mt-6 sm:mt-8 lg:mt-10 max-w-lg text-base sm:text-lg font-light leading-relaxed text-stone-600">
              {heroSubtitle}
            </p>
            <div className="mt-8 sm:mt-10 lg:mt-12 flex flex-col gap-3 sm:gap-4 sm:flex-row">
              <Button data-testid="hero-cta-plans" onClick={() => document.getElementById("plans").scrollIntoView({ behavior: "smooth" })} className="rounded-full bg-stone-900 px-6 sm:px-8 py-5 sm:py-6 text-sm sm:text-base hover:bg-stone-800">
                {heroCTAPrimary}
              </Button>
              <Button data-testid="hero-cta-trial" variant="ghost" onClick={() => document.getElementById("essai").scrollIntoView({ behavior: "smooth" })} className="rounded-full border border-stone-300 px-6 sm:px-8 py-5 sm:py-6 text-sm sm:text-base text-stone-700 hover:border-stone-900 hover:bg-transparent hover:text-stone-900">
                {heroCTASecondary}
              </Button>
            </div>
          </div>
          <div className="relative lg:col-span-5">
            <div className="relative">
              <img
                src="/img/loulou-hero.png"
                alt="Loulou — coach fitness"
                data-testid="hero-image"
                className="h-[560px] w-full object-contain drop-shadow-[0_30px_40px_rgba(26,26,24,0.15)]"
              />
            </div>
            <div className="absolute -bottom-6 -left-6 hidden rounded-xl border border-stone-200 bg-white px-6 py-5 shadow-[0_20px_60px_-20px_rgba(26,26,24,0.15)] sm:block">
              <p className="text-xs uppercase tracking-[0.2em] text-stone-500">Studio</p>
              <p className="font-display text-lg text-stone-900">Lun · Ven — 9h/20h30</p>
              <p className="mt-1 text-xs text-stone-500">Sam — 9h/11h · Accès libre &amp; cours</p>
            </div>
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section id="pourquoi" className="border-t border-stone-200/70 py-16 sm:py-20 lg:py-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-12">
          <div className="mb-10 sm:mb-12 lg:mb-16 max-w-2xl">
            <p className="mb-3 sm:mb-4 text-xs font-medium uppercase tracking-[0.32em] text-stone-500">
              {homeContent.why_title || "Pourquoi nous choisir ?"}
            </p>
            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl font-light leading-tight tracking-tight text-stone-900">
              L&apos;accompagnement, <span className="italic">l&apos;ambiance,</span><br /> et vous.
            </h2>
          </div>

          <div className="grid gap-4 sm:gap-6 md:grid-cols-2 lg:grid-cols-4">
            {(() => {
              // Default icons and tags for each item
              const defaultItems = [
                { Icon: Target, tag: "Suivi personnalisé", tid: "why-suivi" },
                { Icon: HeartHandshake, tag: "Ambiance familiale", tid: "why-ambiance" },
                { Icon: Users, tag: "Communauté", tid: "why-communaute" },
                { Icon: ShieldCheck, tag: "Expertise", tid: "why-expertise" },
              ];

              const whyItems = homeContent.why_items && homeContent.why_items.length > 0 
                ? homeContent.why_items 
                : [
                    { title: "Fait sur mesure", text: "Je m'adapte à vos contraintes, vos objectifs, séance après séance." },
                    { title: "Sans jugement", text: "Un cadre bienveillant et chaleureux pour s'entraîner en confiance, quel que soit votre niveau." },
                    { title: "Ensemble plus loin", text: "Rejoignez un groupe soudé et motivé pour progresser ensemble, dans la durée." },
                    { title: "Équipement de qualité", text: "Matériel professionnel et espace dédié pour un entraînement optimal et sécurisé." },
                  ];

              return whyItems.map((item, index) => {
                const { Icon, tag, tid } = defaultItems[index] || defaultItems[0];
                return (
                  <div
                    key={index}
                    data-testid={tid}
                    className="hover-lift group relative overflow-hidden rounded-xl sm:rounded-2xl border border-stone-200 bg-white p-6 sm:p-8 lg:p-10"
                  >
                    <div className="flex h-12 w-12 sm:h-14 sm:w-14 items-center justify-center rounded-full bg-[#3b82f6]/10 text-[#3b82f6] transition-colors group-hover:bg-[#3b82f6] group-hover:text-white">
                      <Icon className="h-5 w-5 sm:h-6 sm:w-6" strokeWidth={1.75} />
                    </div>
                    <p className="mt-6 sm:mt-8 text-[10px] font-medium uppercase tracking-[0.28em] text-[#8B7D6B]">{tag}</p>
                    <p className="font-display mt-2 text-xl sm:text-2xl font-light text-stone-900">{item.title}</p>
                    <p className="mt-3 sm:mt-4 text-sm leading-relaxed text-stone-600">{item.text || item.description}</p>
                  </div>
                );
              });
            })()}
          </div>
        </div>
      </section>

      {/* COACH BIO */}
      <section id="coach" className="border-t border-stone-200/70 bg-[#EFEFEA]/40 py-24 lg:py-32">
        <div className="mx-auto grid max-w-6xl items-center gap-16 px-6 lg:grid-cols-12 lg:gap-12 lg:px-12">
          <div className="relative lg:col-span-5">
            <div className="relative mx-auto max-w-sm overflow-hidden rounded-2xl border border-stone-200 bg-white">
              <img
                src={
                  homeContent.coach_photo_url
                    ? `${BACKEND_URL}${homeContent.coach_photo_url}?t=${Date.now()}`
                    : "/img/sam-portrait.jpeg"
                }
                alt="Coach"
                data-testid="coach-portrait"
                className="h-[520px] w-full object-cover"
              />
            </div>
            <div className="absolute -bottom-4 left-1/2 hidden -translate-x-1/2 rounded-full border border-stone-200 bg-white px-5 py-2 shadow-[0_20px_60px_-20px_rgba(26,26,24,0.15)] sm:block">
              <p className="text-[10px] uppercase tracking-[0.28em] text-[#8B7D6B]">Fondateur · Coach diplômé</p>
            </div>
          </div>

          <div className="lg:col-span-7">
            <p className="mb-4 text-xs font-medium uppercase tracking-[0.32em] text-stone-500">Le coach</p>
            <h2 className="font-display text-4xl font-light leading-tight tracking-tight text-stone-900 sm:text-5xl whitespace-pre-line">
              {homeContent.about_title || "Ton coach"}
            </h2>
            <div className="mt-8 max-w-xl text-base font-light leading-relaxed text-stone-600 whitespace-pre-line">
              {homeContent.about_description || "Samuel, coach sportif depuis 2021."}
            </div>

            <div data-testid="coach-stats" className="mt-12 grid grid-cols-3 gap-4 border-t border-stone-200 pt-8">
              {[
                { value: `${Math.max(0, new Date().getFullYear() - coachStats.since_year)}+`, label: "années d'expérience" },
                { value: `${coachStats.members_count}+`, label: "clients accompagnés" },
                { value: coachStats.sessions_per_year.toLocaleString("fr-FR"), label: "séances / an" },
              ].map((s) => (
                <div key={s.label}>
                  <p className="font-display text-3xl font-light text-stone-900 sm:text-4xl">{s.value}</p>
                  <p className="mt-2 text-[11px] uppercase tracking-[0.2em] text-stone-500">{s.label}</p>
                </div>
              ))}
            </div>

            <div
              data-testid="coach-certification"
              className="mt-6 inline-flex items-center gap-3 rounded-full border border-[#3b82f6]/40 bg-white px-4 py-2"
            >
              <ShieldCheck className="h-4 w-4 flex-none text-[#3b82f6]" strokeWidth={1.75} />
              <span className="text-xs text-stone-700">
                <span className="uppercase tracking-[0.22em] text-[#8B7D6B]">Certifié</span>
                <span className="mx-2 text-stone-300">·</span>
                BPJEPS AF — Activités de la Forme, mention Musculation
              </span>
            </div>

            {/* Instagram Button - Centré sur mobile */}
            {homeContent?.coach_instagram_url && (
              <div className="mt-6 flex justify-center lg:justify-start">
                <a
                  href={homeContent.coach_instagram_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="coach-instagram-link"
                  className="inline-flex items-center gap-3 rounded-full bg-gradient-to-r from-[#3b82f6] to-[#2563eb] px-6 py-3 text-sm font-medium text-white shadow-lg shadow-[#3b82f6]/25 transition-all duration-300 hover:shadow-xl hover:shadow-[#3b82f6]/40 hover:scale-105"
                >
                  <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path fillRule="evenodd" d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z" clipRule="evenodd" />
                  </svg>
                  <span className="tracking-wide">Suivre sur Instagram</span>
                </a>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* GALLERY */}
      <section id="galerie" className="border-t border-stone-200/70 py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-12">
          <div className="mb-14 flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
            <div>
              <p className="mb-4 text-xs font-medium uppercase tracking-[0.32em] text-stone-500">
                {homeContent.gallery_eyebrow || "Galerie"}
              </p>
              <h2 className="font-display max-w-2xl text-4xl font-light leading-tight tracking-tight text-stone-900 sm:text-5xl">
                {homeContent.gallery_title || "Le studio, au quotidien."}
              </h2>
            </div>
            <p className="max-w-sm text-sm leading-relaxed text-stone-600">
              {homeContent.gallery_description || "Un espace pensé pour la performance, la récupération et les rencontres."}
            </p>
          </div>

          <div className="grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
            {gallery.length === 0 ? Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="aspect-[4/5] rounded-2xl border border-stone-200 bg-stone-100" />
            )) : gallery.map((g, i) => (
              <figure
                key={g.slot}
                data-testid={`gallery-${i + 1}`}
                className={`hover-lift group relative overflow-hidden rounded-2xl border border-stone-200 bg-stone-100 ${
                  i === 0 ? "lg:row-span-2 aspect-[3/4] lg:aspect-auto" : "aspect-[4/5]"
                }`}
              >
                <img
                  src={g.src}
                  alt={g.caption}
                  loading="lazy"
                  className="h-full w-full object-cover grayscale-[15%] transition-all duration-500 group-hover:scale-[1.04] group-hover:grayscale-0"
                />
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-90" />
                <figcaption className="absolute bottom-4 left-4 right-4 flex items-end justify-between">
                  <span className="text-[10px] font-medium uppercase tracking-[0.28em] text-white/85">{g.caption}</span>
                  <span className="rounded-full bg-white/15 px-2 py-1 text-[10px] font-medium text-white/90 backdrop-blur-sm">
                    0{i + 1}
                  </span>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <Testimonials />

      {/* PLANS */}
      <section id="plans" className="border-t border-stone-200/70 bg-[#EFEFEA]/40 py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-12">
          <div className="mb-12 sm:mb-16 flex flex-col justify-between gap-4 sm:gap-6 lg:flex-row lg:items-end">
            <div>
              <p className="mb-3 sm:mb-4 text-xs font-medium uppercase tracking-[0.32em] text-stone-500">Forfaits & séances</p>
              <h2 className="font-display max-w-2xl text-3xl sm:text-4xl lg:text-5xl font-light leading-tight tracking-tight text-stone-900">
                Formules disponibles
              </h2>
            </div>
            <p className="max-w-sm text-sm leading-relaxed text-stone-600">
              Tarifs transparents. Contactez-moi directement pour souscrire au forfait de votre choix.
            </p>
          </div>

          {/* Tabs Navigation */}
          <div className="mb-10 flex justify-center">
            <div
              role="tablist"
              className="inline-flex gap-2 rounded-full border border-stone-200 bg-white p-1.5"
            >
              <button
                data-testid="plan-tab-coaching"
                role="tab"
                aria-selected={planTab === "coaching"}
                onClick={() => setPlanTab("coaching")}
                className={`relative rounded-full px-6 py-3 text-sm font-medium transition-all ${
                  planTab === "coaching" 
                    ? "bg-stone-50 text-stone-900" 
                    : "text-stone-600 hover:text-stone-900"
                }`}
              >
                {planTab === "coaching" && (
                  <div className="absolute inset-x-0 bottom-0 h-0.5 bg-[#3b82f6] rounded-full" />
                )}
                PRÉSENTIEL
              </button>
              <button
                data-testid="plan-tab-libre"
                role="tab"
                aria-selected={planTab === "libre"}
                onClick={() => setPlanTab("libre")}
                className={`relative rounded-full px-6 py-3 text-sm font-medium transition-all ${
                  planTab === "libre" 
                    ? "bg-stone-50 text-stone-900" 
                    : "text-stone-600 hover:text-stone-900"
                }`}
              >
                {planTab === "libre" && (
                  <div className="absolute inset-x-0 bottom-0 h-0.5 bg-[#3b82f6] rounded-full" />
                )}
                ACCÈS LIBRE
              </button>
              <button
                data-testid="plan-tab-distanciel"
                role="tab"
                aria-selected={planTab === "distanciel"}
                onClick={() => setPlanTab("distanciel")}
                className={`relative rounded-full px-6 py-3 text-sm font-medium transition-all ${
                  planTab === "distanciel" 
                    ? "bg-stone-50 text-stone-900" 
                    : "text-stone-600 hover:text-stone-900"
                }`}
              >
                {planTab === "distanciel" && (
                  <div className="absolute inset-x-0 bottom-0 h-0.5 bg-[#3b82f6] rounded-full" />
                )}
                DISTANCIEL
              </button>
            </div>
          </div>

          {/* Plans Grid */}
          {(() => {
            const filtered = plans.filter((p) => {
              if (planTab === "coaching") return p.type === "coaching" || p.type === "individuel";
              if (planTab === "libre") return p.type === "libre";
              if (planTab === "distanciel") return p.type === "remote" || p.type === "distanciel";
              return false;
            });

            return (
              <div className="grid gap-4 sm:gap-6 sm:grid-cols-2 lg:grid-cols-3" data-testid={`plan-grid-${planTab}`}>
                {filtered.map((p) => {
                  const meta = PLAN_META[p.lookup_key] || {};
                  const isPopularCoaching = p.lookup_key === "coaching_2sem_monthly";
                  const isPopularDistanciel = p.lookup_key === "remote_evolution" || p.lookup_key === "distanciel_evolution";
                  const isPopular = isPopularCoaching || isPopularDistanciel || p.is_popular;
                  const isDistanciel = planTab === "distanciel";
                  const isIndividuel = p.type === "individuel";
                  
                  return (
                    <div
                      key={p.lookup_key}
                      data-testid={`plan-card-${p.lookup_key}`}
                      className={`hover-lift group flex flex-col rounded-xl sm:rounded-2xl border p-5 sm:p-8 relative ${
                        isPopular
                          ? "border-[#3b82f6] border-2 bg-gradient-to-br from-stone-900 to-stone-800 text-stone-50 shadow-xl shadow-[#3b82f6]/30" 
                          : "border-stone-200 bg-white text-stone-900"
                      }`}
                    >
                      {isPopular && (
                        <div className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full bg-[#3b82f6] px-4 py-1 text-xs font-medium text-white shadow-lg">
                          ⭐ Populaire
                        </div>
                      )}
                      
                      <div className="mb-4 sm:mb-6">
                        <p className={`text-[10px] font-medium uppercase tracking-[0.28em] ${isPopular ? "text-blue-300" : "text-stone-500"}`}>
                          {isDistanciel ? "Programme en ligne" : meta.tag}
                        </p>
                        <h3 className="font-display mt-2 sm:mt-3 text-lg sm:text-xl font-medium leading-tight">{p.name}</h3>
                        <p className={`mt-1.5 sm:mt-2 text-sm font-light ${isPopular ? "text-stone-300" : "text-stone-500"}`}>
                          {p.description || meta.tagline}
                        </p>
                      </div>
                      
                      <div className="mb-6 sm:mb-8 flex items-baseline gap-1">
                        <span className="font-display text-4xl sm:text-5xl font-light">{p.price}</span>
                        <span className="text-base sm:text-lg">€</span>
                        <span className={`ml-2 text-sm ${isPopular ? "text-stone-300" : "text-stone-500"}`}>
                          {isIndividuel ? "/ séance" : "/ mois"}
                        </span>
                      </div>
                      
                      <ul className={`mb-6 sm:mb-8 flex-1 space-y-2.5 sm:space-y-3 text-sm ${isPopular ? "text-stone-200" : "text-stone-600"}`}>
                        {(p.features || meta.highlights || []).map((feature, idx) => (
                          <li key={idx} className="flex items-start gap-3">
                            <span className={`mt-2 h-px w-4 flex-none ${isPopular ? "bg-blue-400" : "bg-stone-300"}`} />
                            {feature}
                          </li>
                        ))}
                      </ul>
                      
                      <Button
                        onClick={() => {
                          if (isIndividuel) {
                            setPersonalCoachingFormOpen(true);
                          } else {
                            // Scroll to contact section smoothly
                            document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
                          }
                        }}
                        className={`w-full rounded-xl py-6 text-base font-medium transition-all ${
                          isPopular 
                            ? "bg-[#3b82f6] text-white hover:bg-[#2563eb]" 
                            : "bg-stone-900 text-white hover:bg-stone-800"
                        }`}
                      >
                        {isIndividuel ? "Demander une séance" : "Contactez-moi"}
                      </Button>
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </div>
      </section>

      {/* TRIAL - Moved right after plans */}
      <section id="essai" className="py-24 lg:py-32">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-12">
          <div className="grid items-center gap-8 sm:gap-12 rounded-2xl sm:rounded-3xl border border-stone-200 bg-white p-5 sm:p-10 lg:grid-cols-2 lg:p-16">
            <div>
              <p className="mb-4 text-xs font-medium uppercase tracking-[0.32em] text-[#8B7D6B]">Séance d'essai offerte</p>
              <h2 className="font-display text-3xl sm:text-4xl font-light leading-tight tracking-tight text-stone-900 lg:text-5xl">
                Testez avant de <span className="italic">vous engager.</span>
              </h2>
              <p className="mt-4 sm:mt-6 text-sm sm:text-base font-light leading-relaxed text-stone-600">
                Remplissez le formulaire et Samuel vous contactera pour confirmer votre créneau — <span className="text-stone-900">gratuitement</span>. Aucun compte requis, aucun engagement.
              </p>
              <p className="mt-3 text-xs sm:text-sm font-medium text-[#8B7D6B]">
                ℹ️ Donne accès à un cours groupé (hors coaching individuel)
              </p>
            </div>
            <div className="flex flex-col gap-4">
              <div className="rounded-2xl bg-[#F9F9F6] p-6 sm:p-8">
                <div className="mb-6 flex items-center gap-3">
                  <div className="flex h-10 w-10 sm:h-12 sm:w-12 flex-shrink-0 items-center justify-center rounded-full bg-[#8B7D6B]/10">
                    <span className="text-lg sm:text-xl">✓</span>
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-stone-900">1 séance offerte</p>
                    <p className="text-xs text-stone-500">*Sous réserve de disponibilité</p>
                  </div>
                </div>
                <Button 
                  data-testid="trial-cta" 
                  onClick={() => setTrialFormOpen(true)}
                  className="w-full rounded-full bg-[#8B7D6B] py-5 sm:py-6 text-sm sm:text-base px-4 hover:bg-[#7A6C5B]"
                >
                  Demander une séance d'essai
                </Button>
              </div>
              <p className="text-center text-xs text-stone-500 px-2">
                Formulaire simple, aucun compte nécessaire
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Trial Request Form Modal */}
      <TrialRequestForm open={trialFormOpen} onOpenChange={setTrialFormOpen} />
      
      {/* Personal Coaching Request Form Modal */}
      <PersonalCoachingRequestForm open={personalCoachingFormOpen} onOpenChange={setPersonalCoachingFormOpen} />

      {/* HOURS */}
      <section id="horaires" className="border-t border-stone-200/70 bg-[#EFEFEA]/40 py-24 lg:py-32">
        <div className="mx-auto grid max-w-7xl gap-16 px-6 lg:grid-cols-2 lg:px-12">
          <div>
            <p className="mb-4 text-xs font-medium uppercase tracking-[0.32em] text-stone-500">Horaires</p>
            <h2 className="font-display text-4xl font-light leading-tight tracking-tight text-stone-900 sm:text-5xl">
              Du lundi <span className="italic">au vendredi.</span><br />9h — 20h30.
            </h2>
            <p className="mt-8 max-w-md text-base font-light leading-relaxed text-stone-600">
              Un studio, des créneaux d'une heure, un rythme humain. Choisissez votre horaire dans votre espace membre.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {["Lun", "Mar", "Mer", "Jeu", "Ven"].map((d) => (
              <div key={d} className="rounded-xl border border-stone-200 bg-white p-6">
                <p className="text-xs uppercase tracking-[0.24em] text-stone-500">{d}</p>
                <p className="font-display mt-3 text-2xl text-stone-900">9h → 20h30</p>
              </div>
            ))}
            <div className="rounded-xl border border-[#3b82f6]/40 bg-white p-6">
              <p className="text-xs uppercase tracking-[0.24em] text-[#8B7D6B]">Sam</p>
              <p className="font-display mt-3 text-2xl text-stone-900">9h → 11h</p>
              <p className="mt-1 text-[10px] uppercase tracking-[0.22em] text-stone-500">Groupe et individuel</p>
            </div>
            <div className="rounded-xl border border-dashed border-stone-300 p-6 text-stone-500">
              <p className="text-xs uppercase tracking-[0.24em]">Dim</p>
              <p className="font-display mt-3 text-2xl">— Fermé</p>
            </div>
          </div>
        </div>
      </section>

      {/* CONTACT - Moved to bottom */}
      <section id="contact" className="border-t border-stone-200/70 py-24 lg:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-12">
          <div className="rounded-3xl border border-stone-200 bg-white p-8 text-center lg:p-12">
            <p className="text-xs font-medium uppercase tracking-[0.28em] text-[#8B7D6B]">Pour toute souscription ou question</p>
            <h3 className="font-display mt-4 text-2xl font-light text-stone-900 sm:text-3xl">Contactez-moi directement</h3>
            <div className="mt-6 flex flex-col items-center gap-4 sm:flex-row sm:justify-center">
              <a href="tel:0668497750" className="flex items-center gap-2 text-lg font-medium text-stone-900 hover:text-[#8B7D6B] transition-colors">
                ☎️ <span>06.68.49.77.50</span>
              </a>
              <span className="hidden text-stone-300 sm:inline">|</span>
              <a href="mailto:samuel.b1992@gmail.com" className="flex items-center gap-2 text-lg font-medium text-stone-900 hover:text-[#8B7D6B] transition-colors">
                ✉️ <span>samuel.b1992@gmail.com</span>
              </a>
            </div>
            
            {/* Trial CTA Button - Mobile optimisé */}
            <div className="mt-8 pt-8 border-t border-stone-200">
              <p className="mb-4 text-sm text-stone-600">Ou essayez gratuitement avant de vous engager</p>
              <Button
                data-testid="contact-trial-scroll-btn"
                onClick={() => document.getElementById("essai")?.scrollIntoView({ behavior: "smooth" })}
                className="mx-auto rounded-full bg-[#3b82f6] px-6 sm:px-8 py-5 sm:py-6 text-sm sm:text-base font-medium text-white shadow-lg hover:shadow-xl hover:bg-[#2563eb] transition-all duration-300 hover:scale-105 max-w-[280px] sm:max-w-none"
              >
                ✨ Voir la séance d'essai gratuite
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-stone-200 bg-[#F9F9F6] py-12">
        <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-6 px-6 md:flex-row md:items-center lg:px-12">
          <div>
            <p className="font-display text-xl text-stone-900">Loulou Fitness</p>
            <p className="mt-1 text-xs uppercase tracking-[0.28em] text-stone-500">Studio privé · Coaching</p>
          </div>
          <p className="text-xs text-stone-500">© {new Date().getFullYear()} — Loulou Fitness. Paiements sécurisés Stripe.</p>
        </div>
      </footer>
    </div>
  );
}
